import React from 'react';
import Container, { ContainerProps } from '@amzn/awsui-components-core/container';
import Header, { HeaderProps } from '@amzn/awsui-components-core/header';
import './Card.scss';

/**
 * Card - Lightweight Cloudscape Container wrapper for Card blocks
 * 
 * Extends Cloudscape Container with Poly-specific style features:
 * - `title` and `subtitle` props for automatic header creation
 * - `background` prop for brand colors using theme tokens
 * - All other Cloudscape Container props pass through unchanged
 */

export type CardBackgroundPreset = 
  | 'home-header'           // Dark background for hero sections
  | 'orange-accent'         // Orange brand color background
  | 'notification-blue'     // Blue notification background
  | 'notification-green'    // Green notification background
  | 'notification-red'      // Red notification background
  | 'notification-grey'     // Grey notification background
  | 'container-content';    // Light gray background

/**
 * @polySemanticUsage Placeholder for now
 */
export interface CardProps extends Omit<ContainerProps, 'header'> {
  title?: string;
  subtitle?: string;
  /**
   * Heading level of the card header.
   * @polyType SelectList
   * @polyOptions H1=h1,H2=h2,H3=h3,H1 Sticky=awsui-h1-sticky
   */
  headerVariant?: HeaderProps['variant'];
  headerActions?: React.ReactNode;
  headerInfo?: React.ReactNode;
  headerCounter?: string;
  
  /**
   * Background preset. Each resolves to a `--poly-color-card-background-*` theme
   * token; the dark presets also switch the content to Cloudscape dark mode text.
   *
   * @default undefined (white card)
   */
  background?: CardBackgroundPreset;
}

const PRESET_BACKGROUNDS: Record<CardBackgroundPreset, string> = {
  'home-header': 'var(--poly-color-card-background-homeHeader)',
  'orange-accent': 'var(--poly-color-card-background-orangeAccent)',
  'notification-blue': 'var(--poly-color-card-background-notificationBlue)',
  'notification-green': 'var(--poly-color-card-background-notificationGreen)',
  'notification-red': 'var(--poly-color-card-background-notificationRed)',
  'notification-grey': 'var(--poly-color-card-background-notificationGrey)',
  'container-content': 'var(--poly-color-card-background-containerContent)',
};

const DARK_MODE_PRESETS: ReadonlySet<CardBackgroundPreset> = new Set<CardBackgroundPreset>([
  'home-header',
  'orange-accent',
  'notification-blue',
  'notification-green',
  'notification-red',
  'notification-grey',
]);

type ContainerStyle = NonNullable<ContainerProps['style']>;

// Stable class on the Container root; Card.scss pins border-style on it because
// border-style has no inline path through the Style API.
const CARD_CLASS = 'card';

// Container's Style API writes these as real inline styles, so the resting chrome
// does not depend on Cloudscape's hashed class rules being present at first paint.
// In visual-refresh mode the default Container has a real 1px border, so its
// color and width are pinned too; presets keep the same width with a transparent
// color so the box does not resize on hydration.
const DEFAULT_CONTAINER_STYLE: ContainerStyle = {
  root: {
    background: 'var(--poly-color-card-background)',
    borderColor: 'var(--poly-color-card-border)',
    borderWidth: 'var(--poly-size-card-borderWidth)',
    borderRadius: 'var(--poly-border-radius-card)',
  },
};

const presetContainerStyle = (background: CardBackgroundPreset): ContainerStyle => ({
  root: {
    background: PRESET_BACKGROUNDS[background],
    borderColor: 'transparent',
    borderWidth: 'var(--poly-size-card-borderWidth)',
    borderRadius: 'var(--poly-border-radius-card)',
  },
});

// A caller's own `style` still wins: its root values layer over the resting
// chrome and its other slots (content, header, footer) pass through untouched.
const withRestingChrome = (
  chrome: ContainerStyle,
  style: ContainerProps['style'],
): ContainerStyle => ({
  ...style,
  root: { ...chrome.root, ...style?.root },
});

export const Card: React.FC<CardProps> = ({
  title,
  subtitle,
  headerVariant = 'h1',
  headerActions,
  headerInfo,
  headerCounter,
  background,
  children,
  style,
  className,
  ...containerProps
}) => {
  const containerClassName = className ? `${CARD_CLASS} ${className}` : CARD_CLASS;

  // Create header only if title is provided
  const header = title ? (
    <Header
      variant={headerVariant}
      description={subtitle}
      actions={headerActions}
      info={headerInfo}
      counter={headerCounter}
    >
      {title}
    </Header>
  ) : undefined;

  if (background) {
    const darkModeClass = DARK_MODE_PRESETS.has(background) ? ' awsui-polaris-dark-mode' : '';

    return (
      <div className={`card-wrapper card-wrapper--${background}${darkModeClass}`}>
        <Container
          {...containerProps}
          className={containerClassName}
          header={header}
          style={withRestingChrome(presetContainerStyle(background), style)}
        >
          {children}
        </Container>
      </div>
    );
  }

  return (
    <Container
      {...containerProps}
      className={containerClassName}
      header={header}
      style={withRestingChrome(DEFAULT_CONTAINER_STYLE, style)}
    >
      {children}
    </Container>
  );
};
